// frontend/src/components/charts/Charts.js
// The Charts slide of the spec: Individual task progress, Task type wise,
// Engineer Workload, plus status/priority split and a completion trend.
import React, { useState, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, IconButton, Tooltip, Alert, Skeleton,
    ToggleButton, ToggleButtonGroup, LinearProgress, Chip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';

import useAutoRefresh from '../../hooks/useAutoRefresh';
import { getCharts } from '../../services/endpoints';
import { STATUS_COLORS, PRIORITY_COLORS, TASK_TYPE_COLORS } from '../../constants/domain';

const REFRESH_MS = Number(process.env.REACT_APP_REFRESH_MS) || 15 * 60 * 1000;

const Panel = ({ title, subtitle, children, height = 340, action }) => (
    <Paper sx={{ p: 2, height, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
                <Typography variant="h6" fontWeight={600}>{title}</Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                )}
            </Box>
            {action}
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
);

const Empty = ({ message }) => (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Typography color="text.secondary">{message}</Typography>
    </Box>
);

/** Horizontal progress bars, matching the spec's bar-and-percentage look. */
const ProgressBars = ({ rows, colorFor, emptyMessage }) => {
    if (!rows || rows.length === 0) return <Empty message={emptyMessage} />;
    return (
        <Box sx={{ overflow: 'auto', height: '100%', pr: 1 }}>
            {rows.map((r) => (
                <Box key={r.name} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {r.name}
                            </Typography>
                            {r.task_type && (
                                <Chip label={r.task_type} size="small" variant="outlined"
                                    sx={{ height: 18, fontSize: 10 }} />
                            )}
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {r.parts} part{r.parts === 1 ? '' : 's'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={700}>{r.progress}%</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, r.progress)}
                        sx={{
                            height: 10, borderRadius: 5,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: colorFor ? colorFor(r) : undefined,
                            },
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
};

const Charts = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [workloadMode, setWorkloadMode] = useState('open');

    const fetch = useCallback(async () => {
        try {
            const res = await getCharts();
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    }, []);

    const { lastRefresh, refreshing, refresh } = useAutoRefresh(fetch, REFRESH_MS);

    if (!data && !error) {
        return (
            <Grid container spacing={2}>
                {[...Array(4)].map((_, i) => (
                    <Grid item xs={12} md={6} key={i}>
                        <Skeleton variant="rounded" height={340} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    const d = data || {};
    const workload = (d.engineer_workload || []).map(w => ({
        ...w,
        name: w.name,
        value: workloadMode === 'open' ? w.open
             : workloadMode === 'completed' ? w.completed
             : w.total,
    }));

    const statusPie = (d.status_distribution || []).map(s => ({
        name: s.status, value: s.count, fill: STATUS_COLORS[s.status] || '#9e9e9e',
    }));
    const priorityPie = (d.priority_distribution || []).map(p => ({
        name: p.priority, value: p.count, fill: PRIORITY_COLORS[p.priority] || '#9e9e9e',
    }));

    return (
        <Box>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Charts</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Project progress and workload
                        {lastRefresh && ` — updated ${lastRefresh.toLocaleTimeString('en-GB')}`}
                    </Typography>
                </Box>
                <Tooltip title="Refresh now">
                    <IconButton onClick={refresh} disabled={refreshing}><Refresh /></IconButton>
                </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
                {/* Individual task progress */}
                <Grid item xs={12} md={6}>
                    <Panel title="Individual task"
                        subtitle="Average completion per task (PSG-859, GPDS-2205, ...)">
                        <ProgressBars
                            rows={d.individual_tasks}
                            colorFor={(r) => TASK_TYPE_COLORS[r.task_type] || '#1976d2'}
                            emptyMessage="No parts have a task reference yet."
                        />
                    </Panel>
                </Grid>

                {/* Task type wise */}
                <Grid item xs={12} md={6}>
                    <Panel title="Task type wise"
                        subtitle="Average completion by PSG / GPDS / Other task">
                        <ProgressBars
                            rows={d.task_type_wise}
                            colorFor={(r) => TASK_TYPE_COLORS[r.name] || '#1976d2'}
                            emptyMessage="No parts yet."
                        />
                    </Panel>
                </Grid>

                {/* Engineer workload */}
                <Grid item xs={12} md={8}>
                    <Panel
                        title="Engineer Workload"
                        subtitle="Parts per engineer"
                        action={
                            <ToggleButtonGroup size="small" exclusive value={workloadMode}
                                onChange={(e, v) => v && setWorkloadMode(v)}>
                                <ToggleButton value="open">Open</ToggleButton>
                                <ToggleButton value="completed">Completed</ToggleButton>
                                <ToggleButton value="total">Total</ToggleButton>
                            </ToggleButtonGroup>
                        }
                    >
                        {workload.length === 0 ? <Empty message="No engineers found." /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={workload} layout="vertical"
                                    margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" width={100}
                                        tick={{ fontSize: 12 }} />
                                    <ReTooltip
                                        formatter={(v) => [`${v} part${v === 1 ? '' : 's'}`, workloadMode]}
                                    />
                                    <Bar dataKey="value" name={workloadMode}
                                        fill="#1b3f94" radius={[0, 4, 4, 0]} barSize={18} />
                                    <Bar dataKey="overdue" name="overdue"
                                        fill="#e53935" radius={[0, 4, 4, 0]} barSize={18} />
                                    <Legend />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>
                </Grid>

                {/* Status split */}
                <Grid item xs={12} md={4}>
                    <Panel title="Status split" subtitle="All parts by status">
                        {statusPie.length === 0 ? <Empty message="No parts yet." /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={statusPie} dataKey="value" nameKey="name"
                                        cx="50%" cy="45%" outerRadius={80} innerRadius={40}
                                        paddingAngle={2}>
                                        {statusPie.map((s, i) => <Cell key={i} fill={s.fill} />)}
                                    </Pie>
                                    <ReTooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>
                </Grid>

                {/* Completion trend */}
                <Grid item xs={12} md={8}>
                    <Panel title="Completion trend" subtitle="Parts completed per week (last 12 weeks)">
                        {(d.completion_trend || []).length === 0
                            ? <Empty message="No parts completed in the last 12 weeks." />
                            : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={d.completion_trend}
                                        margin={{ left: 0, right: 16, top: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                        <YAxis allowDecimals={false} />
                                        <ReTooltip />
                                        <Line type="monotone" dataKey="completed" stroke="#43a047"
                                            strokeWidth={2} dot={{ r: 3 }} name="Completed" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                    </Panel>
                </Grid>

                {/* Priority split */}
                <Grid item xs={12} md={4}>
                    <Panel title="Priority split" subtitle="All parts by priority">
                        {priorityPie.length === 0 ? <Empty message="No parts yet." /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={priorityPie} dataKey="value" nameKey="name"
                                        cx="50%" cy="45%" outerRadius={80} label>
                                        {priorityPie.map((p, i) => <Cell key={i} fill={p.fill} />)}
                                    </Pie>
                                    <ReTooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Charts;
